import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import {
  useHandLibrary,
  useHandDetail,
  useInstallHand,
  useForkHand,
  type HandSummary,
} from "../../hooks/useHands";
import { useWorkspace } from "../../hooks/useWorkspace";
import { MarkdownText, hasMarkdown } from "../../components/review/MarkdownText";

const CATEGORIES = ["All", "Sales", "Marketing", "Operations", "Finance", "Strategy", "HR", "Legal", "General"];
const VERTICALS = ["All", "General", "Wealth Management", "Healthcare", "Logistics", "IT Services & BPO", "Professional Services", "HR"];

const MODEL_LABEL: Record<string, string> = {
  "claude-haiku-4-5-20251001": "Haiku",
  "claude-sonnet-4-6": "Sonnet",
  "claude-opus-4-7": "Opus",
};

const ICON_FALLBACK = "✦";

// Map CXO role tags to the most relevant hands category
const ROLE_TO_CATEGORY: Record<string, string> = {
  cfo: "Finance",
  "vp finance": "Finance",
  "head of finance": "Finance",
  coo: "Operations",
  "vp operations": "Operations",
  "head of operations": "Operations",
  cso: "Sales",
  "vp sales": "Sales",
  "head of sales": "Sales",
  cmo: "Marketing",
  "vp marketing": "Marketing",
  "head of marketing": "Marketing",
  "chief marketing officer": "Marketing",
  "growth": "Marketing",
  ceo: "Strategy",
  "managing director": "Strategy",
  "general manager": "Strategy",
  chro: "HR",
  "vp hr": "HR",
  "vp people": "HR",
  "head of hr": "HR",
  "head of people": "HR",
  clo: "Legal",
  "general counsel": "Legal",
  "vp legal": "Legal",
};

function defaultCategoryFromRole(cxoRoleTag: string): string {
  const key = cxoRoleTag.toLowerCase().trim();
  return ROLE_TO_CATEGORY[key] ?? "All";
}

function categoryColor(cat: string): string {
  const map: Record<string, string> = {
    sales: "#fff0f6",
    operations: "#f0fff4",
    finance: "#fefbe8",
    strategy: "#f5f0ff",
    hr: "#f0f6ff",
    legal: "#fff5f0",
    general: "#f6f5f2",
  };
  return map[cat.toLowerCase()] ?? "#f6f5f2";
}

const PAGE_SIZE = 24;

export function HandsLibrary() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const wsQ = useWorkspace(workspaceId!);

  const [scope, setScope] = useState<"public" | "company">("public");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [vertical, setVertical] = useState("All");
  const [page, setPage] = useState(1);
  const [categorySetByRole, setCategorySetByRole] = useState(false);
  const [selectedHandId, setSelectedHandId] = useState<string | null>(null);

  // Once workspace loads, default category to the CXO's area (only once)
  useEffect(() => {
    if (!categorySetByRole && wsQ.data?.workspace.cxoRoleTag) {
      const defaultCat = defaultCategoryFromRole(wsQ.data.workspace.cxoRoleTag);
      if (defaultCat !== "All") {
        setCategory(defaultCat);
        setCategorySetByRole(true);
      }
    }
  }, [wsQ.data, categorySetByRole]);

  // Debounce search so we don't fire a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [scope, category, vertical, debouncedSearch]);

  const libraryQ = useHandLibrary(workspaceId, {
    scope,
    category,
    vertical,
    search: debouncedSearch,
    page,
    limit: PAGE_SIZE,
  });

  const installMut = useInstallHand(workspaceId);
  const forkMut = useForkHand(workspaceId);

  const { hands = [], total = 0, pages = 1 } = libraryQ.data ?? {};

  const handleInstall = async (e: React.MouseEvent, handId: string) => {
    e.stopPropagation();
    await installMut.mutateAsync(handId);
  };

  const handleFork = async (e: React.MouseEvent, handId: string) => {
    e.stopPropagation();
    const result = await forkMut.mutateAsync(handId);
    navigate(`/workspaces/${workspaceId}/hands/builder/${result.draftId}`);
  };

  return (
    <div className="main-inner">
      {selectedHandId && (
        <HandDetailPanel
          workspaceId={workspaceId!}
          handId={selectedHandId}
          onClose={() => setSelectedHandId(null)}
          onForkNavigate={(draftId) => navigate(`/workspaces/${workspaceId}/hands/builder/${draftId}`)}
        />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div className="greet">Hands Library</div>
        <div style={{ flex: 1 }} />
        {total > 0 && (
          <div style={{ fontSize: 12, color: "var(--text-3)" }}>
            {total.toLocaleString()} agent{total !== 1 ? "s" : ""}
          </div>
        )}
        <button
          type="button"
          className="btn primary"
          onClick={() => navigate(`/workspaces/${workspaceId}/hands/builder`)}
        >
          + New Hand
        </button>
      </div>
      <div className="greet-sub">
        Discover and install AI agents. Public hands are Xamun-curated; Company hands are private to your workspace.
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginTop: 20 }}>
        <button
          type="button"
          className={`tab ${scope === "public" ? "active" : ""}`}
          onClick={() => setScope("public")}
        >
          Public Library
        </button>
        <button
          type="button"
          className={`tab ${scope === "company" ? "active" : ""}`}
          onClick={() => setScope("company")}
        >
          Company Library
        </button>
        <button
          type="button"
          className="tab"
          onClick={() => navigate(`/workspaces/${workspaceId}/hands/my`)}
        >
          My Hands
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, alignItems: "center" }}>
        <input
          className="input"
          style={{ flex: 1, maxWidth: 300 }}
          placeholder="Search agents…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="select"
          style={{ width: 150 }}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {CATEGORIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select
          className="select"
          style={{ width: 170 }}
          value={vertical}
          onChange={(e) => setVertical(e.target.value)}
        >
          {VERTICALS.map((v) => (
            <option key={v} value={v}>{v === "All" ? "All Industries" : v}</option>
          ))}
        </select>
        {wsQ.data?.workspace.cxoRoleTag && category !== "All" && (
          <button
            type="button"
            className="btn sm"
            style={{ fontSize: 11, color: "var(--text-3)" }}
            onClick={() => setCategory("All")}
          >
            Show all
          </button>
        )}
      </div>

      {/* Grid */}
      {libraryQ.isLoading && !libraryQ.data ? (
        <LibrarySkeleton />
      ) : hands.length === 0 ? (
        <EmptyLibrary scope={scope} onNew={() => navigate(`/workspaces/${workspaceId}/hands/builder`)} />
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 14,
              opacity: libraryQ.isFetching ? 0.6 : 1,
              transition: "opacity 150ms",
            }}
          >
            {hands.map((h) => (
              <HandCard
                key={h.id}
                hand={h}
                onInstall={(e) => handleInstall(e, h.id)}
                onFork={(e) => handleFork(e, h.id)}
                installing={installMut.isPending}
                forking={forkMut.isPending}
                onClick={() => setSelectedHandId(h.id)}
              />
            ))}
          </div>

          {pages > 1 && (
            <Pagination page={page} pages={pages} total={total} limit={PAGE_SIZE} onChange={setPage} />
          )}
        </>
      )}
    </div>
  );
}

function Pagination({
  page,
  pages,
  total,
  limit,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  limit: number;
  onChange: (p: number) => void;
}) {
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // Show at most 7 page buttons with ellipsis
  const pageNums: (number | "…")[] = [];
  if (pages <= 7) {
    for (let i = 1; i <= pages; i++) pageNums.push(i);
  } else {
    pageNums.push(1);
    if (page > 3) pageNums.push("…");
    for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) pageNums.push(i);
    if (page < pages - 2) pageNums.push("…");
    pageNums.push(pages);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 28, paddingBottom: 8 }}>
      <button
        type="button"
        className="btn sm"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        style={{ minWidth: 72 }}
      >
        ← Prev
      </button>

      {pageNums.map((n, i) =>
        n === "…" ? (
          <span key={`ellipsis-${i}`} style={{ fontSize: 12, color: "var(--text-3)", padding: "0 4px" }}>…</span>
        ) : (
          <button
            key={n}
            type="button"
            className={`btn sm ${n === page ? "primary" : ""}`}
            onClick={() => onChange(n)}
            style={{ minWidth: 32 }}
          >
            {n}
          </button>
        )
      )}

      <button
        type="button"
        className="btn sm"
        disabled={page >= pages}
        onClick={() => onChange(page + 1)}
        style={{ minWidth: 72 }}
      >
        Next →
      </button>

      <span style={{ fontSize: 11, color: "var(--text-3)", marginLeft: 8 }}>
        {from}–{to} of {total.toLocaleString()}
      </span>
    </div>
  );
}

function HandCard({
  hand,
  onInstall,
  onFork,
  installing,
  forking,
  onClick,
}: {
  hand: HandSummary;
  onInstall: (e: React.MouseEvent) => void;
  onFork: (e: React.MouseEvent) => void;
  installing: boolean;
  forking: boolean;
  onClick: () => void;
}) {
  const modelLabel = MODEL_LABEL[hand.model] ?? hand.model;
  const bg = categoryColor(hand.category);

  return (
    <div className="lib-card" onClick={onClick} style={{ cursor: "pointer" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 10 }}>
        <div
          className="lib-icon"
          style={{ background: bg, width: 34, height: 34, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}
        >
          {hand.icon ?? ICON_FALLBACK}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>{hand.name}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
            {hand.category} · {hand.scope === "public" ? "Xamun" : "Company"}
          </div>
        </div>
        <span style={{ fontSize: 10, color: "var(--text-3)", marginTop: 2, flexShrink: 0 }}>View ›</span>
      </div>

      <div style={{ fontSize: 12, color: "var(--text-2)", lineHeight: 1.5, marginBottom: 12, minHeight: 48 }}>
        {hand.when_to_use ?? hand.description}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {hand.is_installed ? (
          <span className="pill green">✓ Installed</span>
        ) : null}
        <span className="pill gray">v{hand.version}</span>
        <span className="pill blue">{modelLabel}</span>
        {hand.install_count > 0 ? (
          <span className="pill gray">{hand.install_count.toLocaleString()} installs</span>
        ) : null}
        {hand.pass_rate !== null ? (
          <span className="pill" style={{ background: "#e4f4ec", color: "#2e7d4f" }}>
            {hand.pass_rate}% evals
          </span>
        ) : null}
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        {!hand.is_installed ? (
          <button
            type="button"
            className="btn primary sm"
            onClick={(e) => { e.stopPropagation(); onInstall(e); }}
            disabled={installing}
            style={{ flex: 1 }}
          >
            Install
          </button>
        ) : (
          <span className="btn sm" style={{ flex: 1, justifyContent: "center", background: "var(--bg-soft)", cursor: "default", color: "var(--text-3)" }}>
            Installed
          </span>
        )}
        <button
          type="button"
          className="btn sm"
          onClick={(e) => { e.stopPropagation(); onFork(e); }}
          disabled={forking}
          data-tip="Fork and customize this Hand in the Builder"
        >
          Fork &amp; customize
        </button>
      </div>
    </div>
  );
}

function EmptyLibrary({ scope, onNew }: { scope: string; onNew: () => void }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 0", color: "var(--text-3)" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6, color: "var(--text-2)" }}>
        {scope === "company" ? "No company hands yet" : "No public hands found"}
      </div>
      <div style={{ fontSize: 12, marginBottom: 20 }}>
        {scope === "company"
          ? "Build a custom Hand in the Builder and publish it to your company library."
          : "No results match your filters."}
      </div>
      {scope === "company" ? (
        <button type="button" className="btn primary" onClick={onNew}>
          + New Hand
        </button>
      ) : null}
    </div>
  );
}

function LibrarySkeleton() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{ height: 180, borderRadius: 10, background: "var(--bg-soft)", opacity: 0.6 }}
        />
      ))}
    </div>
  );
}

const TOOL_LABEL: Record<string, string> = {
  web_search: "Web Search",
  web_fetch: "Web Fetch",
  bash: "Bash",
  code_execution: "Code Execution",
};

function HandDetailPanel({
  workspaceId,
  handId,
  onClose,
  onForkNavigate,
}: {
  workspaceId: string;
  handId: string;
  onClose: () => void;
  onForkNavigate: (draftId: string) => void;
}) {
  const detailQ = useHandDetail(workspaceId, handId);
  const forkMut = useForkHand(workspaceId);
  const installMut = useInstallHand(workspaceId);

  const [tryOpen, setTryOpen] = useState(false);
  const [tryInput, setTryInput] = useState("");
  const [tryRunning, setTryRunning] = useState(false);
  const [tryResult, setTryResult] = useState<{ output: string; model: string; input_tokens: number; output_tokens: number; latency_ms: number } | null>(null);
  const [tryError, setTryError] = useState<string | null>(null);
  const tryOutputRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (scrollTimerRef.current !== null) clearTimeout(scrollTimerRef.current); }, []);

  const handleTryRun = async () => {
    if (!tryInput.trim() || tryRunning) return;
    setTryRunning(true);
    setTryResult(null);
    setTryError(null);
    try {
      const resp = await fetch(`/api/hands/${handId}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: tryInput }),
      });
      const data = await resp.json();
      if (!resp.ok) { setTryError(data.error ?? "Run failed"); return; }
      setTryResult(data);
      scrollTimerRef.current = setTimeout(() => tryOutputRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50);
    } catch (e) {
      setTryError(e instanceof Error ? e.message : "Network error");
    } finally {
      setTryRunning(false);
    }
  };

  const hand = detailQ.data;
  const modelLabel = hand ? (MODEL_LABEL[hand.model] ?? hand.model) : "";
  const bg = hand ? categoryColor(hand.category) : "#f6f5f2";

  const handleInstall = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await installMut.mutateAsync(handId);
  };

  const handleFork = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const result = await forkMut.mutateAsync(handId);
    onClose();
    onForkNavigate(result.draftId);
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)",
          zIndex: 1000, backdropFilter: "blur(2px)",
        }}
      />
      {/* Drawer */}
      <div
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: 500,
          background: "var(--bg)", boxShadow: "-4px 0 32px rgba(0,0,0,0.18)",
          zIndex: 1001, display: "flex", flexDirection: "column", overflowY: "auto",
        }}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {hand && (
              <div
                style={{ background: bg, width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}
              >
                {hand.icon ?? ICON_FALLBACK}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                {hand?.name ?? <span style={{ color: "var(--text-3)" }}>Loading…</span>}
              </div>
              {hand && (
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
                  {hand.category} · {hand.scope === "public" ? "Xamun" : "Company"} · v{hand.version}
                </div>
              )}
            </div>
            <button
              type="button"
              className="btn sm"
              onClick={onClose}
              style={{ flexShrink: 0, fontSize: 16, padding: "2px 10px" }}
            >
              ✕
            </button>
          </div>

          {hand && (
            <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
              {!hand.is_installed ? (
                <button
                  type="button"
                  className="btn primary sm"
                  onClick={handleInstall}
                  disabled={installMut.isPending}
                  style={{ flex: 1 }}
                >
                  {installMut.isPending ? "Installing…" : "Install"}
                </button>
              ) : (
                <span className="btn sm" style={{ flex: 1, justifyContent: "center", background: "var(--bg-soft)", cursor: "default", color: "var(--text-3)" }}>
                  ✓ Installed
                </span>
              )}
              <button
                type="button"
                className="btn sm"
                onClick={handleFork}
                disabled={forkMut.isPending}
                style={{ flex: 1 }}
              >
                {forkMut.isPending ? "Forking…" : "Fork & customize"}
              </button>
              <button
                type="button"
                className="btn sm"
                onClick={() => { setTryOpen(o => !o); setTryResult(null); setTryError(null); }}
                style={{ flexShrink: 0, background: tryOpen ? "var(--accent-soft)" : undefined, color: tryOpen ? "var(--accent)" : undefined }}
              >
                ▷ Try it
              </button>
            </div>
          )}
        </div>

        {/* Body */}
        {detailQ.isLoading ? (
          <div style={{ padding: 24, color: "var(--text-3)", fontSize: 13 }}>Loading…</div>
        ) : !hand ? (
          <div style={{ padding: 24, color: "var(--text-3)", fontSize: 13 }}>Not found.</div>
        ) : (
          <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 22 }}>

            {/* Description / When to use */}
            <section>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>When to use</div>
              <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.6 }}>
                {hand.when_to_use ?? hand.description}
              </div>
            </section>

            {/* Badges */}
            <section>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                <span className="pill blue">{modelLabel}</span>
                <span className="pill gray">v{hand.version}</span>
                {hand.install_count > 0 && <span className="pill gray">{hand.install_count.toLocaleString()} installs</span>}
                {hand.pass_rate !== null && (
                  <span className="pill" style={{ background: "#e4f4ec", color: "#2e7d4f" }}>{hand.pass_rate}% evals</span>
                )}
                {hand.is_installed && <span className="pill green">✓ Installed</span>}
              </div>
            </section>

            {/* Tools */}
            {Array.isArray(hand.allowed_tools) && hand.allowed_tools.length > 0 && (
              <section>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Tools</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {hand.allowed_tools.map((t) => (
                    <span key={t} className="pill" style={{ background: "#f0f6ff", color: "#2563eb" }}>
                      {TOOL_LABEL[t] ?? t}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* System Prompt */}
            <section>
              <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>System Prompt</div>
              <pre style={{
                fontSize: 11.5, lineHeight: 1.65, color: "var(--text-2)",
                background: "var(--bg-soft)", borderRadius: 8, padding: "12px 14px",
                whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
                maxHeight: 340, overflowY: "auto",
              }}>
                {hand.system_prompt || <span style={{ color: "var(--text-3)" }}>No system prompt.</span>}
              </pre>
            </section>

            {/* Try it */}
            {tryOpen && (
              <section style={{ borderTop: "1px solid var(--border)", paddingTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>▷ Try it</div>
                <textarea
                  value={tryInput}
                  onChange={e => setTryInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleTryRun(); }}
                  placeholder={`Describe what you want ${hand.name} to do…\n\nTip: paste realistic inputs matching the agent's INPUTS contract.`}
                  rows={5}
                  style={{
                    width: "100%", boxSizing: "border-box", resize: "vertical",
                    fontFamily: "inherit", fontSize: 12.5, lineHeight: 1.55,
                    border: "1px solid var(--border)", borderRadius: 8,
                    padding: "10px 12px", outline: "none", color: "var(--text)",
                    background: "var(--bg-soft)",
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn primary sm"
                    onClick={handleTryRun}
                    disabled={tryRunning || !tryInput.trim()}
                  >
                    {tryRunning ? "Running…" : "Run  ⌘↵"}
                  </button>
                </div>
                {tryError && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--danger-soft)", color: "var(--danger)", borderRadius: 7, fontSize: 12.5 }}>
                    {tryError}
                  </div>
                )}
                {tryResult && (
                  <div ref={tryOutputRef} style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Output</div>
                      <div style={{ fontSize: 10, color: "var(--text-3)" }}>
                        {tryResult.input_tokens}↑ {tryResult.output_tokens}↓ tok · {tryResult.latency_ms}ms · {tryResult.model}
                      </div>
                    </div>
                    {hasMarkdown(tryResult.output) ? (
                      <div style={{
                        background: "var(--bg-soft)",
                        borderRadius: 8, padding: "12px 14px",
                        maxHeight: 440, overflowY: "auto",
                      }}>
                        <MarkdownText>{tryResult.output}</MarkdownText>
                      </div>
                    ) : (
                      <pre style={{
                        fontSize: 12, lineHeight: 1.65,
                        background: "#1a1f2e", color: "#d4d8e8",
                        borderRadius: 8, padding: "12px 14px",
                        whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
                        maxHeight: 440, overflowY: "auto",
                      }}>
                        {tryResult.output}
                      </pre>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Meta */}
            <section style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
              {[
                ["Category", hand.category],
                ["Model", modelLabel],
                ["Max turns", String(hand.max_turns)],
                ["Timeout", `${hand.timeout_ms / 1000}s`],
                ["Data class", hand.data_classification],
                ["PII handling", hand.pii_handling],
                ["Cost tier", hand.cost_tier],
              ].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize: 10, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
                  <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 1 }}>{value}</div>
                </div>
              ))}
            </section>
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
